import React, { useState, useCallback } from 'react'
import Cropper from 'react-easy-crop'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import { PhotoIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline'

interface AvatarUploaderProps {
  currentAvatarUrl: string | null
  onFileChange: (file: File) => void
  onRemove: () => void
}

export const AvatarUploader: React.FC<AvatarUploaderProps> = ({
  currentAvatarUrl,
  onFileChange,
  onRemove,
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentAvatarUrl)

  const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const readFile = (file: File) => {
    return new Promise<string>((resolve) => {
      const reader = new FileReader()
      reader.addEventListener('load', () => resolve(reader.result as string), false)
      reader.readAsDataURL(file)
    })
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0]
      const imageDataUrl = await readFile(file)
      setImageSrc(imageDataUrl)
      setIsDialogOpen(true)
      // Reset input value so same file can be selected again if needed
      e.target.value = ''
    }
  }

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image()
      image.addEventListener('load', () => resolve(image))
      image.addEventListener('error', (error) => reject(error))
      image.setAttribute('crossOrigin', 'anonymous')
      image.src = url
    })

  const getCroppedImg = async (imageSrc: string, pixelCrop: any): Promise<Blob | null> => {
    const image = await createImage(imageSrc)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      return null
    }

    // Limit the output size to 1000x1000 to save bandwidth and server memory
    const MAX_SIZE = 1000
    let width = pixelCrop.width
    let height = pixelCrop.height

    if (width > MAX_SIZE || height > MAX_SIZE) {
      const ratio = width / height
      if (width > height) {
        width = MAX_SIZE
        height = width / ratio
      } else {
        height = MAX_SIZE
        width = height * ratio
      }
    }

    // Setting canvas.width/height clears any existing drawn content.
    canvas.width = width
    canvas.height = height

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      width,
      height,
    )

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob)
      }, 'image/jpeg')
    })
  }

  const handleSave = async () => {
    try {
      if (imageSrc && croppedAreaPixels) {
        const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels)
        if (croppedBlob) {
          const file = new File([croppedBlob], 'avatar.jpg', { type: 'image/jpeg' })
          onFileChange(file)
          setPreviewUrl(URL.createObjectURL(croppedBlob))
          setIsDialogOpen(false)
          setImageSrc(null)
        }
      }
    } catch (e) {
      console.error(e)
    }
  }

  const handleCancel = () => {
    setIsDialogOpen(false)
    setImageSrc(null)
  }

  const handleRemove = () => {
    setPreviewUrl(null)
    onRemove()
  }

  return (
    <div className="col-span-full">
      <label className="block text-sm/6 font-medium text-[color:var(--theme-fg)]">Photo</label>
      <div className="mt-2 flex items-center gap-x-3">
        {previewUrl ? (
          <img
            className="h-12 w-12 rounded-full object-cover bg-[color:var(--theme-card)]"
            src={previewUrl}
            alt="Current avatar"
          />
        ) : (
          <PhotoIcon className="h-12 w-12 text-[color:var(--theme-muted)]" aria-hidden="true" />
        )}

        <label
          htmlFor="avatar-upload"
          className="rounded-md bg-[color:var(--theme-hover)] px-3 py-2 text-sm font-semibold text-[color:var(--theme-fg)] shadow-sm hover:bg-[color:var(--theme-hover-strong)] cursor-pointer"
        >
          Change
          <input
            id="avatar-upload"
            name="avatar-upload"
            type="file"
            className="sr-only"
            accept="image/png, image/jpeg, image/jpg"
            onChange={handleFileChange}
          />
        </label>

        {previewUrl && (
          <button
            type="button"
            onClick={handleRemove}
            className="rounded-md bg-[color:var(--theme-hover)] px-3 py-2 text-sm font-semibold text-[color:var(--theme-fg)] shadow-sm hover:bg-[color:var(--theme-hover-strong)]"
          >
            Remove
          </button>
        )}
      </div>

      <Dialog open={isDialogOpen} onClose={handleCancel} className="relative z-50">
        <div className="fixed inset-0 bg-[color:var(--theme-overlay-strong)]" aria-hidden="true" />
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <DialogPanel className="theme-surface w-full max-w-lg space-y-4 rounded-xl p-6 border border-[color:var(--theme-border-soft)]">
            <DialogTitle className="font-bold text-[color:var(--theme-fg)] text-lg">
              Crop your photo
            </DialogTitle>

            <div className="relative h-64 w-full bg-[color:var(--theme-bg)] rounded-lg overflow-hidden">
              {imageSrc && (
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                  cropShape="round"
                  showGrid={false}
                />
              )}
            </div>

            <div className="flex items-center gap-4">
              <span className="text-[color:var(--theme-fg)] text-sm">Zoom</span>
              <input
                type="range"
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                aria-labelledby="Zoom"
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full h-2 bg-[color:var(--theme-card)] rounded-lg appearance-none cursor-pointer"
              />
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="rounded-md bg-[color:var(--theme-hover)] px-3 py-2 text-sm font-semibold text-[color:var(--theme-fg)] hover:bg-[color:var(--theme-hover-strong)]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="rounded-md bg-[color:var(--theme-accent-strong)] px-3 py-2 text-sm font-semibold text-[color:var(--theme-bg)] hover:opacity-90"
              >
                Save
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  )
}
